import { useState } from 'react';
import { Alert, Button, CheckboxField, TextField } from '../ui';
import { DEFAULT_VISIBILITY, type CardVisit } from '../../lib/userDetail';
import type { CardVisitRequest } from '../../api/apiSchemas';

type FieldKey = 'email' | 'phone' | 'website' | 'linkedin' | 'address' | 'bio';

interface FieldState {
  value: string;
  visible: boolean;
}
interface SocialState {
  platform: string;
  url: string;
  visible: boolean;
}

const FIELD_META: { key: FieldKey; label: string; type?: string; placeholder?: string }[] = [
  { key: 'email', label: 'Email công việc', type: 'email' },
  { key: 'phone', label: 'Số điện thoại / Zalo', type: 'tel' },
  { key: 'website', label: 'Website / Portfolio', placeholder: 'https://…' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/…' },
  { key: 'address', label: 'Địa chỉ / Văn phòng' },
];

function isHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function initField(card: CardVisit, key: FieldKey): FieldState {
  const existing = card[key];
  if (existing) return { value: existing.value, visible: existing.visible };
  return { value: '', visible: DEFAULT_VISIBILITY[key] };
}

export interface CardVisitFormProps {
  defaultCard: CardVisit;
  defaultCardPublic: boolean;
  onSubmit: (payload: { card: CardVisitRequest; cardPublic: boolean }) => void;
  submitting: boolean;
  errorMessage?: string | null;
}

export function CardVisitForm({
  defaultCard,
  defaultCardPublic,
  onSubmit,
  submitting,
  errorMessage,
}: CardVisitFormProps) {
  const [fields, setFields] = useState<Record<FieldKey, FieldState>>({
    email: initField(defaultCard, 'email'),
    phone: initField(defaultCard, 'phone'),
    website: initField(defaultCard, 'website'),
    linkedin: initField(defaultCard, 'linkedin'),
    address: initField(defaultCard, 'address'),
    bio: initField(defaultCard, 'bio'),
  });
  const [socials, setSocials] = useState<SocialState[]>(
    (defaultCard.socials ?? []).map((s) => ({ ...s })),
  );
  const [cardPublic, setCardPublic] = useState(defaultCardPublic);
  const [localError, setLocalError] = useState<string | null>(null);

  const setField = (key: FieldKey, patch: Partial<FieldState>) =>
    setFields((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  function validate(): string | null {
    if (fields.email.value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.value.trim())) {
      return 'Email không hợp lệ.';
    }
    for (const key of ['website', 'linkedin'] as const) {
      const v = fields[key].value.trim();
      if (v && !isHttpUrl(v)) return `${key === 'website' ? 'Website' : 'LinkedIn'} phải là URL http(s).`;
    }
    for (const s of socials) {
      if (s.url.trim() && !isHttpUrl(s.url.trim())) return 'Liên kết mạng xã hội phải là URL http(s).';
    }
    return null;
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const err = validate();
    setLocalError(err);
    if (err) return;
    const card: CardVisitRequest = {};
    for (const { key } of FIELD_META) {
      const f = fields[key];
      if (f.value.trim() !== '') card[key] = { value: f.value.trim(), visible: f.visible };
    }
    if (fields.bio.value.trim() !== '') {
      card.bio = { value: fields.bio.value.trim(), visible: fields.bio.visible };
    }
    const cleanSocials = socials
      .filter((s) => s.url.trim() !== '')
      .map((s) => ({ platform: s.platform.trim(), url: s.url.trim(), visible: s.visible }));
    if (cleanSocials.length > 0) card.socials = cleanSocials;
    onSubmit({ card, cardPublic });
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      {errorMessage ? (
        <Alert tone="error" live>
          {errorMessage}
        </Alert>
      ) : null}
      {localError ? (
        <Alert tone="error" live>
          {localError}
        </Alert>
      ) : null}

      <p className="form-note">
        Mỗi trường có công tắc <strong>Hiển thị công khai</strong> riêng. Mặc định email, số điện thoại
        và địa chỉ được <strong>ẩn</strong> để bảo vệ quyền riêng tư — bật khi bạn muốn chia sẻ.
      </p>

      {FIELD_META.map(({ key, label, type, placeholder }) => (
        <div className="card-field" key={key}>
          <TextField
            label={label}
            type={type}
            placeholder={placeholder}
            value={fields[key].value}
            onChange={(e) => setField(key, { value: e.target.value })}
          />
          <CheckboxField
            label="Hiển thị công khai"
            checked={fields[key].visible}
            onChange={(e) => setField(key, { visible: e.target.checked })}
          />
        </div>
      ))}

      <div className="card-field">
        <div className="field">
          <label className="field__label" htmlFor="card-bio">
            Giới thiệu ngắn (bio)
          </label>
          <textarea
            id="card-bio"
            className="field__input"
            rows={3}
            maxLength={300}
            value={fields.bio.value}
            onChange={(e) => setField('bio', { value: e.target.value })}
          />
        </div>
        <CheckboxField
          label="Hiển thị công khai"
          checked={fields.bio.visible}
          onChange={(e) => setField('bio', { visible: e.target.checked })}
        />
      </div>

      <h2 className="form-section__title">Mạng xã hội khác</h2>
      {socials.map((s, i) => (
        <div className="card-field" key={i}>
          <TextField
            label="Nền tảng"
            placeholder="github, x, facebook…"
            value={s.platform}
            onChange={(e) =>
              setSocials((prev) => prev.map((it, j) => (j === i ? { ...it, platform: e.target.value } : it)))
            }
          />
          <TextField
            label="Liên kết"
            placeholder="https://…"
            value={s.url}
            onChange={(e) =>
              setSocials((prev) => prev.map((it, j) => (j === i ? { ...it, url: e.target.value } : it)))
            }
          />
          <CheckboxField
            label="Hiển thị công khai"
            checked={s.visible}
            onChange={(e) =>
              setSocials((prev) => prev.map((it, j) => (j === i ? { ...it, visible: e.target.checked } : it)))
            }
          />
          <Button
            type="button"
            variant="ghost"
            onClick={() => setSocials((prev) => prev.filter((_, j) => j !== i))}
          >
            Xóa
          </Button>
        </div>
      ))}
      {socials.length < 10 ? (
        <Button
          type="button"
          variant="secondary"
          onClick={() => setSocials((prev) => [...prev, { platform: '', url: '', visible: false }])}
        >
          + Thêm mạng xã hội
        </Button>
      ) : null}

      <h2 className="form-section__title">Công khai thẻ</h2>
      <CheckboxField
        label="Cho phép hiển thị trang thẻ công khai (/u/handle) và API công khai"
        checked={cardPublic}
        onChange={(e) => setCardPublic(e.target.checked)}
      />

      <Button type="submit" loading={submitting} disabled={submitting}>
        Lưu card visit
      </Button>
    </form>
  );
}
